import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import useSeo from '@/hooks/useSeo';
import { topics } from '@/mocks/topics';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/site';

const SITE_URL = typeof window !== 'undefined' ? window.location.origin : '';
const catName = (id: string) => categories.find((c) => c.id === id)?.name ?? '专题';

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = topics.find((t) => t.id === id);

  const list = topic ? articles.filter((a) => a.topicId === topic.id) : [];
  const related = topics.filter((t) => t.id !== id && t.categoryId === topic?.categoryId).slice(0, 3);
  const moreRelated =
    related.length < 3 ? topics.filter((t) => t.id !== id).slice(0, 3 - related.length) : [];
  const recommend = [...related, ...moreRelated].slice(0, 3);

  const jsonLd = topic
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: topic.title,
          description: topic.summary,
          url: `${SITE_URL}/topic/${topic.id}`,
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: '资源库', item: `${SITE_URL}/library` },
            {
              '@type': 'ListItem',
              position: 3,
              name: topic.title,
              item: `${SITE_URL}/topic/${topic.id}`,
            },
          ],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: topic.title,
          itemListElement: list.map((a, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: a.title,
            url: `${SITE_URL}/article/${a.id}`,
          })),
        },
      ]
    : [];

  useSeo({
    title: topic
      ? `${topic.title} - 在线看料专题合集｜看料社`
      : '专题不存在｜看料社',
    description: topic
      ? `${topic.summary} 本专题共整理 ${topic.articleCount} 篇内容。`
      : '该专题可能已被合并或调整，请返回资源库浏览其他在线看料专题。',
    keywords: topic
      ? `在线看料专题,${topic.tags.join(',')},在线看料系列,在线看料完整版`
      : '在线看料专题,看料资源库',
    type: 'article',
    jsonLd,
  });

  if (!topic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 md:pt-20 max-w-content mx-auto px-4 md:px-6 py-24 text-center">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-5">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">没有找到这个专题</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已经被合并或调整，去资源库看看其他内容吧。</p>
          <Link
            to="/library"
            className="inline-block mt-6 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
          >
            返回资源库
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="relative w-full min-h-[380px] md:min-h-[460px] flex items-end overflow-hidden">
          <img
            src={topic.cover}
            alt={`${topic.title} - 在线看料专题封面`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/25" />
          <div className="relative z-10 w-full max-w-content mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumbs
              items={[
                { label: '首页', to: '/' },
                { label: '资源库', to: '/library' },
                { label: catName(topic.categoryId) },
                { label: topic.title },
              ]}
            />
            <span className="inline-block mt-5 px-2.5 py-1 rounded-md text-xs bg-accent-500 text-secondary-950">
              {catName(topic.categoryId)}
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-background-50 leading-snug max-w-4xl">
              {topic.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-background-100/85">
              <span className="flex items-center gap-1.5">
                <i className="ri-file-list-3-line" />
                {topic.articleCount} 篇整理
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line" />
                更新于 {topic.updateTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line text-accent-400" />
                热度 {topic.hotScore}
              </span>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-content mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                专题简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{topic.summary}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {topic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/library?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <h2 className="mt-10 font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                合集目录
              </h2>
              <div className="flex flex-col gap-4">
                {list.map((a, idx) => (
                  <Link
                    key={a.id}
                    to={`/article/${a.id}`}
                    className="group flex gap-4 md:gap-5 p-4 md:p-5 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                  >
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600 font-heading font-bold">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {a.title}
                      </span>
                      <span className="block mt-2 text-sm text-foreground-600 line-clamp-2">
                        {a.excerpt}
                      </span>
                      <span className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                        <span className="flex items-center gap-1.5">
                          <i className="ri-calendar-line" />
                          {a.publishTime}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <i className="ri-time-line" />
                          约 {a.readMinutes} 分钟
                        </span>
                      </span>
                    </span>
                    <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-600 transition-colors self-center hidden md:block" />
                  </Link>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-24 flex flex-col gap-6">
                <div className="p-5 rounded-lg bg-secondary-950 text-background-50">
                  <h3 className="font-heading text-lg font-bold mb-3">关于这个专题</h3>
                  <p className="text-sm text-background-200/90 leading-relaxed">
                    本专题由看料社编辑部整理，按时间顺序梳理事件脉络。内容会随进展持续更新，建议收藏后定期回看。
                  </p>
                  <div className="mt-4 pt-4 border-t border-background-50/15 flex flex-col gap-2 text-sm text-background-200/85">
                    <span className="flex items-center gap-2">
                      <i className="ri-calendar-check-line text-accent-400" />
                      最近更新：{topic.updateTime}
                    </span>
                    <span className="flex items-center gap-2">
                      <i className="ri-bookmark-line text-accent-400" />
                      共 {topic.articleCount} 篇整理
                    </span>
                  </div>
                </div>

                <div className="p-5 rounded-lg border border-background-200 bg-background-50">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">相关专题推荐</h3>
                  <div className="flex flex-col gap-3">
                    {recommend.map((t) => (
                      <Link
                        key={t.id}
                        to={`/topic/${t.id}`}
                        className="group flex flex-col gap-1 p-3 rounded-md hover:bg-background-100 transition-colors"
                      >
                        <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {t.title}
                        </span>
                        <span className="text-xs text-foreground-500">
                          {catName(t.categoryId)} · {t.articleCount} 篇
                        </span>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/library"
                    className="mt-4 flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors"
                  >
                    浏览全部专题
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}